import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getOfferById,
  downloadOfferPdf,
  downloadLegalCertificate,
  submitReformulation,
  reformulateOffer,
  downloadReformulationFile,
  deleteReformulationFile,
  deleteOfferFile,
  downloadOfferFile,
  markOfferAsRead,
  initiateSigningProcess,
  getSigningStatus,
  downloadSignedAgreement,
} from "../services/api";
import { FONTS, COLORS, BREAKPOINTS } from "../utils/styleConstants";
import sidebarLogo from "../assets/sidebar_logo.svg";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import OfferTimeline from "../components/OfferTimeline";
import RichTextEditor from "../components/common/RichTextEditor";
import SigningAddressModal from "../components/SigningAddressModal";
import { formatDateToCustomFormat } from '../utils/dateUtils';
import { formatAmountForDisplay } from '../utils/amountUtils';

const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background-color: var(--secondary-color);
  display: flex;
  box-shadow: var(--box-shadow);
  overflow: hidden;
  height: 100vh;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    flex-direction: column;
    box-shadow: none;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem 3rem;
  border-radius: 1rem 0 0 1rem;
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    height: auto;
    min-height: calc(100vh - 70px);
    padding: 1.5rem;
    border-radius: 2rem 2rem 0 0;
    overflow-y: auto;
    background: white;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
  width: 100%;
  padding-left: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    padding-left: 1.5rem;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const HeaderTitle = styled.h1`
  color: ${COLORS.ACCENT};
  font-size: 2.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const StatusInTitle = styled.span`
  color: ${COLORS.PRIMARY};
  font-size: 2.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  text-transform: lowercase;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const OfferIdText = styled.div`
  color: ${COLORS.PRIMARY};
  font-size: 1.2rem;
  font-weight: 400;
  font-family: ${FONTS.SECONDARY};
  margin-top: 0.5rem;
  opacity: 0.7;
`;

const StatusBadge = styled.span`
  display: none;
`;

const Description = styled.p`
  color: ${COLORS.ACCENT};
  font-size: 1.1rem;
  font-family: ${FONTS.PRIMARY};
  margin: 0;
  line-height: 1.5;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    font-size: 1rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-end;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    width: 100%;
    align-items: stretch;
  }
`;

// Botón de Editar - fondo blanco, borde naranja, texto naranja (OutlineButton style)
const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 1.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  transition: all 0.2s;
  background-color: transparent;
  color: #ff7057;
  border: 1px solid #ff7057;

  &:hover {
    background-color: #fff0e8;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 112, 87, 0.5);
  }

  &:disabled {
    color: #a0a0a0;
    border-color: #a0a0a0;
    cursor: not-allowed;
  }
`;

// Botón de descarga - texto naranja con subrayado (DownloadLink style)
const DownloadButton = styled.button`
  background-color: transparent;
  color: #ff7057;
  border: none;
  text-decoration: underline;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  padding: 0.5rem 0;

  &:hover {
    color: #e06542;
  }

  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

// Mantener ActionButton original para otros usos
const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 1.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${(props) =>
    props.primary
      ? `
    background-color: #ff7057;
    color: white;
    
    &:hover {
      background-color: #e55a41;
    }
  `
      : `
    background-color: white;
    color: #a8b9d5;
    border: 1px solid #dbe4f2;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    
    &:hover {
      background-color: #f5f8fd;
    }
  `}

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Botón de firma electrónica - estilo verde para diferenciarlo
const SigningButton = styled.button`
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 1.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background-color: #45a049;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.3);
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

// Estilos copiados de OfertaStep1 para mantener consistencia
const HorizontalSections = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1.5rem;
  border-radius: 8px;
  background-color: white;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-weight: 600;
  min-height: 2.5em;
  display: flex;
  align-items: center;
`;

const SectionDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  font-family: ${FONTS.SECONDARY};
  min-height: 3em;
`;

const HorizontalField = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 1rem;
  gap: 1rem;

  &.last-field {
    margin-bottom: 0.5rem;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 2rem;
    border-bottom: 1px solid #bad1e6;
  }
`;

const FieldLabel = styled.label`
  font-size: 0.9rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  display: flex;
  align-items: center;
  min-width: 40%;
  white-space: nowrap;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    width: 100%;
    white-space: normal;
  }
`;

const FieldValue = styled.div`
  padding: 0.25rem 0;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  width: 100%;
  text-align: right;
  color: ${COLORS.SECONDARY};
  font-weight: 600;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    text-align: left;
    width: 100%;
  }
`;

const FormWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 1rem;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 22px;
  margin-right: 0;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #4d7af7;
  }

  &:checked + span:before {
    transform: translateX(22px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e2e2e2;
  transition: 0.3s;
  border-radius: 34px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

// Estilos copiados de OfertaStep2 para las secciones de contenido
const FilePreview = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-top: 1rem;
`;

const FileIcon = styled.div`
  margin-right: 1rem;
  color: #ff6b35;
  font-size: 1.5rem;
`;

const EditorFileName = styled.p`
  flex: 1;
  font-size: 0.9rem;
  color: #333;
  margin: 0;
`;

const EditorRemoveFileButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;

  &:hover {
    color: #e41717;
  }
`;

const EditorArea = styled.textarea`
  width: 100%;
  min-height: 300px;
  border: 1px solid #75a9f9;
  border-radius: 1rem;
  padding: 1rem;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  resize: none;
  margin-bottom: 0.5rem;

  &:focus {
    outline: none;
    border-color: #5a94f5;
  }

  &::placeholder {
    color: #a0aec0;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1d3f0;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8c1e9;
  }
`;

const ClipIcon = styled.div`
  margin-right: 0.5rem;
  color: #75a9f9;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    transform: rotate(-45deg);
  }
`;

const ArrowIcon = styled.div`
  margin-left: 0.5rem;
  color: #75a9f9;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 10px;
    height: 10px;
    fill: currentColor;
  }
`;

const AdjuntarButton = styled.button`
  display: flex;
  align-items: center;
  background-color: transparent;
  color: #75a9f9;
  border: none;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const FileInputHidden = styled.input`
  display: none;
`;

// Styled components adicionales para reformulación con archivos
const DisabledAdjuntarButton = styled(AdjuntarButton)`
  opacity: 0.5;
  cursor: not-allowed;

  &:hover {
    text-decoration: none;
  }
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 0.25rem;
  margin-left: 0.5rem;

  &:hover {
    color: #e41717;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FileName = styled.p`
  flex: 1;
  font-size: 0.9rem;
  color: #333;
  margin: 0;
`;

const ErrorMessage = styled.div`
  color: #e41717;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  font-family: ${FONTS.SECONDARY};
`;

const InfoMessage = styled.div`
  padding: 0.75rem;
  border-radius: 4px;
  margin-top: 0.5rem;
  background-color: #fff8f6;
  border: 1px solid #ffcdb7;
  color: #b54800;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
`;

// Nuevo contenedor para el layout de dos columnas de contenido
const ContentLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

// Estilos necesarios que se mantienen
const ConfidentialText = styled.span`
  font-size: 1.3rem;
  font-weight: 800;
  color: #ff7057;
  font-family: ${FONTS.SECONDARY};
  margin-left: 0.5rem;
`;

const CheckboxInput = styled.input`
  margin-right: 8px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const ConfirmationCheckbox = styled.div`
  display: flex;
  align-items: flex-start;
  width: 70%;

  label {
    font-size: 0.9rem;
    font-family: ${FONTS.SECONDARY};
    color: ${COLORS.SECONDARY};
    cursor: pointer;

    a {
      color: ${COLORS.PRIMARY};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 0.5rem;
`;

const ErrorBanner = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  border: 1px solid #f5c6cb;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.1rem;
  color: ${COLORS.SECONDARY};
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  font-size: 0.9rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;

  &:after {
    content: ${(props) => (props.required ? '"*"' : '""')};
    color: ${COLORS.PRIMARY};
    margin-left: 4px;
  }
`;

const DateInput = styled.input`
  padding: 0.25rem 0;
  border: none;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  outline: none;
  background-color: transparent;
  width: 100%;
  text-align: right;

  &:focus {
    outline: none;
    border-bottom-color: ${COLORS.PRIMARY};
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    font-size: 0.9rem;
    text-align: left;
    width: 100%;
  }
`;

const DateField = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 1rem;
  gap: 1rem;

  &:focus-within {
    border-bottom-color: ${COLORS.PRIMARY};
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 2rem;
    border-bottom: 1px solid #bad1e6;
  }
`;

const DateLabel = styled.label`
  font-size: 0.9rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  display: flex;
  align-items: center;
  min-width: 40%;
  white-space: nowrap;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    width: 100%;
    white-space: normal;
  }
`;

const ReformulationActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1rem;
  margin-top: 1rem;
`;

const ReformulationButton = styled.button`
  width: auto;
  min-width: 200px;
  justify-content: center;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 1.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  transition: all 0.2s;
  background-color: #ff7057;
  color: white;
  border: none;
  box-shadow: 0 2px 4px rgba(255, 112, 87, 0.2);

  &:hover {
    background-color: #ff5b40;
  }

  &:disabled {
    background-color: #ffc1ad;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const SignedIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 1rem;
  font-size: 0.9rem;
  color: #ff7057;
  font-weight: 500;
`;

const SignedIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #ff7057;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 0.75rem;
  font-weight: bold;
  flex-shrink: 0;
`;

const SectionTitleWithSignature = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const OfferDetailPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estados para edición en ofertas contraofertadas
  const [reformulationText, setReformulationText] = useState("");
  const [reformulationFiles, setReformulationFiles] = useState([]);
  const [acceptsLegalTerms, setAcceptsLegalTerms] = useState(false);
  const [newDeadline, setNewDeadline] = useState("");
  
  // Estado para eliminar archivos
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  const [isDeletingReformulationFile, setIsDeletingReformulationFile] = useState(false);
  
  // Estados para firma electrónica
  const [isSigningModalOpen, setIsSigningModalOpen] = useState(false);
  const [isInitiatingSigning, setIsInitiatingSigning] = useState(false);
  const [signingError, setSigningError] = useState(null);

  // Función helper para detectar si el contenido del RichTextEditor está realmente vacío
  const isRichTextEmpty = (htmlContent) => {
    if (!htmlContent) return true;
    
    // Crear un elemento temporal para parsear el HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Obtener solo el texto sin tags HTML
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // Verificar si el texto está vacío después de quitar espacios y saltos de línea
    return textContent.trim().length === 0;
  };

  // Función helper para formatear fecha manteniendo zona horaria local
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      // Usar los métodos locales para evitar problemas de zona horaria
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date for input:", error);
      return '';
    }
  };

  useEffect(() => {
    fetchOfferData();
  }, [id]);

  // Pre-rellenar nueva fecha límite con deadlineToRespond cuando es contraofertada
  useEffect(() => {
    if (offer && offer.status === "counteroffered" && offer.deadlineToRespond && !newDeadline) {
      const formattedDate = formatDateForInput(offer.deadlineToRespond);
      if (formattedDate) {
        setNewDeadline(formattedDate);
      }
    }
  }, [offer, newDeadline]);

  // Función para obtener la fecha mínima permitida para la nueva fecha límite
  const getMinDeadlineDate = () => {
    if (offer && offer.deadlineToRespond) {
      const formattedDate = formatDateForInput(offer.deadlineToRespond);
      if (formattedDate) {
        return formattedDate;
      }
    }
    
    // Fallback a mañana si no hay deadlineToRespond o hay error
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDateForInput(tomorrow.toISOString());
  };

  const fetchOfferData = async () => {
    try {
      setLoading(true);
      const response = await getOfferById(id);
      setOffer(response);
      setError(null);
      
      // Si la oferta está marcada como no leída, marcarla como leída
      if (response && response.is_unread) {
        try {
          await markOfferAsRead(id);
          // Actualizar el estado local para reflejar el cambio
          setOffer(prevOffer => ({ ...prevOffer, is_unread: false }));
        } catch (markError) {
          console.error("Error marking offer as read:", markError);
          // No mostrar error al usuario, esto no es crítico
        }
      }
    } catch (error) {
      console.error("Error fetching offer:", error);
      setError("Error al cargar los detalles de la oferta");
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      draft: "Borrador",
      sent: "Enviada",
      accepted: "Aceptada",
      rejected: "Rechazada",
      expired: "Vencida",
      counteroffered: "Contraofertada",
      expiring_soon: "Vence en 5 días",
      reformulated: "Reformulado",
      reformulation_accepted: "Reformulado aceptado",
      reformulation_rejected: "Reformulado rechazado",
      signing: "Firmando",
      partial_signed: "Firmado parcialmente",
      signed: "Firmado",
    };
    return statusMap[status] || status;
  };

  const getPageTitle = (status) => {
    const titleMap = {
      draft: "Oferta Vinculante: borrador",
      sent: "Oferta Vinculante: enviada",
      accepted: "Oferta Vinculante: aceptada",
      rejected: "Oferta Vinculante: vencida",
      expired: "Oferta Vinculante: vencida",
      counteroffered: "Oferta Vinculante: contraofertada",
      expiring_soon: "Oferta Vinculante: vence en 5 días",
    };
    return titleMap[status] || "Detalle de Oferta";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { day: "numeric", month: "long", year: "numeric" };
    return new Date(dateString).toLocaleDateString("es-ES", options);
    //return formatDateToCustomFormat(dateString);
  };

  // Usar la función unificada de amountUtils
  const formatAmount = formatAmountForDisplay;

  const handleDownloadPDF = async () => {
    try {
      // No pasar token para usuarios autenticados
      await downloadOfferPdf(id);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const handleDownloadLegalCertificate = async () => {
    try {
      await downloadLegalCertificate(id);
    } catch (error) {
      console.error("Error downloading legal certificate:", error);
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      await downloadOfferFile(offer.id, file.id, null, file.name);
    } catch (error) {
      console.error("Error downloading file:", error);
      setError(error.message || "Error al descargar el archivo");
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!canEdit()) {
      console.warn("Solo se pueden eliminar archivos de ofertas en estado borrador");
      return;
    }

    setIsDeletingFile(true);
    try {
      const response = await deleteOfferFile(id, fileId);
      
      // Actualizar la oferta con los archivos restantes
      setOffer(prevOffer => ({
        ...prevOffer,
        files: response.remainingFiles || []
      }));
      
    } catch (error) {
      console.error("Error deleting file:", error);
      setError(error.message || "Error al eliminar el archivo");
    } finally {
      setIsDeletingFile(false);
    }
  };

  // Funciones para manejar archivos de reformulación
  const handleReformulationFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Validar tipos de archivo
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      setError("Solo se admiten archivos PDF o Word (.doc, .docx)");
      // Limpiar el input file
      e.target.value = '';
      return;
    }

    // Validar cantidad máxima (5 archivos)
    if (files.length > 5) {
      setError("Máximo 5 archivos permitidos");
      // Limpiar el input file
      e.target.value = '';
      return;
    }

    // Limpiar el contenido del editor cuando se adjuntan archivos
    setReformulationText("");
    setReformulationFiles(files);
    setError(null);
    
    // Limpiar el input file para permitir seleccionar el mismo archivo de nuevo más tarde
    e.target.value = '';
  };

  const handleRemoveReformulationFile = (index) => {
    const newFiles = reformulationFiles.filter((_, i) => i !== index);
    setReformulationFiles(newFiles);
    
    // Si se eliminan todos los archivos, limpiar cualquier error relacionado con archivos
    // y limpiar el input file para permitir seleccionar archivos de nuevo
    if (newFiles.length === 0) {
      setError(null);
      // Limpiar el input file
      const fileInput = document.getElementById("reformulation-file-upload");
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  const handleDownloadReformulationFile = async (file) => {
    try {
      await downloadReformulationFile(offer.id, file.reformulation_file_id || file.id, file.name);
    } catch (error) {
      console.error("Error downloading reformulation file:", error);
      setError(error.message || "Error al descargar el archivo de reformulación");
    }
  };

  const handleDeleteReformulationFile = async (fileId) => {
    setIsDeletingReformulationFile(true);
    try {
      const response = await deleteReformulationFile(id, fileId);
      
      // Actualizar la oferta con los archivos restantes
      setOffer(prevOffer => ({
        ...prevOffer,
        reformulationFiles: response.remainingFiles || []
      }));
      
    } catch (error) {
      console.error("Error deleting reformulation file:", error);
      setError(error.message || "Error al eliminar el archivo de reformulación");
    } finally {
      setIsDeletingReformulationFile(false);
    }
  };

  const handleSubmitReformulation = async () => {
    if (!acceptsLegalTerms) {
      console.warn("Debe aceptar los términos legales para continuar");
      return;
    }

    // Validar que se proporcione contenido (texto O archivos, no ambos)
    const hasText = !isRichTextEmpty(reformulationText);
    const hasFiles = reformulationFiles.length > 0;

    if (!hasText && !hasFiles) {
      console.warn("Debe proporcionar contenido escrito o adjuntar archivos para la reformulación");
      return;
    }

    if (hasText && hasFiles) {
      console.warn("Solo puede usar texto O archivos, no ambos");
      return;
    }

    if (!newDeadline) {
      console.warn("Debe seleccionar una nueva fecha límite para la reformulación");
      return;
    }

    // Validar que la nueva fecha sea futura
    const selectedDate = new Date(newDeadline);
    
    // Obtener la fecha mínima permitida (deadlineToRespond original)
    let minAllowedDate;
    if (offer && offer.deadlineToRespond) {
      minAllowedDate = new Date(offer.deadlineToRespond);
      // Restar un día para dar margen
      minAllowedDate.setDate(minAllowedDate.getDate() - 1);
    } else {
      // Fallback a hoy si no hay deadlineToRespond
      minAllowedDate = new Date();
      minAllowedDate.setHours(0, 0, 0, 0);
    }

    if (selectedDate < minAllowedDate) {
      console.warn("La nueva fecha límite debe ser igual o posterior a la fecha límite original");
      return;
    }

    try {
      setIsSubmitting(true);

      let reformulationData;
      let includesFiles = false;

      if (hasFiles) {
        // Reformulación con archivos
        reformulationData = {
          files: reformulationFiles,
          newDeadline: newDeadline
        };
        includesFiles = true;
      } else {
        // Reformulación con texto
        reformulationData = {
          reformulationText: reformulationText.trim(),
          newDeadline: newDeadline
        };
      }

      // Usar el nuevo endpoint de reformulación
      await reformulateOffer(id, reformulationData, includesFiles);
      
      // Limpiar el formulario
      setReformulationText("");
      setReformulationFiles([]);
      setNewDeadline("");
      setAcceptsLegalTerms(false);
      
      // Limpiar el input file
      const fileInput = document.getElementById("reformulation-file-upload");
      if (fileInput) {
        fileInput.value = '';
      }

      // Redirigir a la pestaña "Enviados" después de enviar
      navigate("/offers");
    } catch (error) {
      console.error("Error enviando reformulación:", error);

      let errorMessage = "Error desconocido";
      if (error.message) {
        errorMessage = error.message;
      }
      
      setError("Error al enviar la reformulación: " + errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Funciones para la firma electrónica
  const canShowSigningButton = () => {
    return offer && ['accepted', 'reformulation_accepted'].includes(offer.status);
  };

  const canShowDownloadSignedAgreement = () => {
    return offer && offer.status === 'signed';
  };

  const isSigningInProgress = () => {
    return offer && ['signing', 'partial_signed'].includes(offer.status);
  };

  const handleOpenSigningModal = () => {
    setSigningError(null);
    setIsSigningModalOpen(true);
  };

  const handleCloseSigningModal = () => {
    setIsSigningModalOpen(false);
    setSigningError(null);
  };

  const handleInitiateSigning = async (signingData) => {
    setIsInitiatingSigning(true);
    setSigningError(null);

    try {
      const result = await initiateSigningProcess(id, signingData);
      
      if (result.success) {
        // Cerrar modal y actualizar oferta
        setIsSigningModalOpen(false);
        // Actualizar la oferta para reflejar el nuevo estado
        await fetchOfferData();
      }
    } catch (error) {
      console.error('Error iniciating signing process:', error);
      
      let errorMessage = 'Error al iniciar el proceso de firma';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSigningError(errorMessage);
    } finally {
      setIsInitiatingSigning(false);
    }
  };

  const handleDownloadSignedAgreement = async () => {
    try {
      await downloadSignedAgreement(id);
    } catch (error) {
      console.error('Error downloading signed agreement:', error);
      
      let errorMessage = 'Error al descargar el acuerdo firmado';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    }
  };

  const canShowDownloadPDF = () => {
    return [
      "draft",
      "sent",
      "accepted",
      "rejected",
      "expired",
      "counteroffered",
      "expiring_soon",
      "reformulated",
      "reformulation_accepted",
      "reformulation_rejected",
      "signing",
      "partial_signed",
      "signed",
    ].includes(offer?.status);
  };

  const canShowLegalCertificate = () => {
    const isValidStatus = [
      "rejected", 
      "expired",
      "reformulation_rejected"
    ].includes(offer?.status);
    
    // Para todos los estados válidos, verificar el campo canShowLegalCertificateButton
    if (isValidStatus) {
      return offer?.canShowLegalCertificateButton === true;
    }
    
    return false;
  };

  const canEdit = () => {
    return offer?.status === "draft";
  };

  const canShowCounterOffer = () => {
    // Mostrar contraoferta si el estado es counteroffered O si hay contenido de contraoferta
    return offer?.status === "counteroffered" || 
           offer?.counteroffer_text || 
           offer?.counterofferText ||
           (offer?.counterOfferFiles && offer.counterOfferFiles.length > 0);
  };

  const shouldShowReformulation = () => {
    // Solo mostrar formulario de reformulación para estado counteroffered
    return offer?.status === "counteroffered";
  };

  const shouldShowReformulationReadOnly = () => {
    // Mostrar reformulación en modo lectura si hay contenido de reformulación
    // y NO está en estado counteroffered (para evitar duplicados)
    const hasReformulationContent = offer?.reformulation_text || 
                                   offer?.reformulationText ||
                                   offer?.reformulation_content ||
                                   (offer?.reformulationFiles && offer.reformulationFiles.length > 0);
    
    return hasReformulationContent && offer?.status !== "counteroffered";
  };

  const isReadOnlyReformulatedState = () => {
    return [
      "reformulated",
      "reformulation_accepted", 
      "reformulation_rejected"
    ].includes(offer?.status);
  };

  const shouldShowRepresentacionContraparte = () => {
    // Mostrar siempre que haya datos del abogado/representante
    return (
      (offer?.lawyer || offer?.lawyer_name) &&
      (offer?.lawyer?.firstName ||
        offer?.lawyer_name ||
        offer?.lawyer?.lastNames ||
        offer?.lawyer_last_name)
    );
  };

  const shouldShowSignatureIndicators = () => {
    return ['signing', 'partial_signed', 'signed'].includes(offer?.status);
  };

  const getSignerStatus = (role) => {
    if (!offer?.signaturesInfo?.signers) return { isSigned: false };
    
    const signer = offer.signaturesInfo.signers.find(s => s.role === role);
    return {
      isSigned: signer?.status === 'signed',
      signedAt: signer?.signedAt,
      name: signer?.name
    };
  };

  const renderSectionTitle = (title, signerRole = null, isToggleContainer = false) => {
    if (!shouldShowSignatureIndicators() || !signerRole) {
      return isToggleContainer ? (
        <SectionTitle style={{ minHeight: "auto" }}>{title}</SectionTitle>
      ) : (
        <SectionTitle>{title}</SectionTitle>
      );
    }

    const signerStatus = getSignerStatus(signerRole);
    
    return (
      <SectionTitleWithSignature>
        <SectionTitle style={isToggleContainer ? { margin: 0, minHeight: "auto" } : { margin: 0 }}>
          {title}
        </SectionTitle>
        {signerStatus.isSigned && (
          <SignedIndicator>
            <SignedIcon>✓</SignedIcon>
            Firmado
          </SignedIndicator>
        )}
      </SectionTitleWithSignature>
    );
  };

  // Función para renderizar campos en el estilo de OfertaStep1
  const renderReadOnlyField = (label, value, isLast = false) => {
    return (
      <HorizontalField className={isLast ? "last-field" : ""}>
        <FieldLabel>{label}</FieldLabel>
        <FieldValue>{value || "-"}</FieldValue>
      </HorizontalField>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <Sidebar>
          <img
            src={sidebarLogo}
            alt="Logo"
            style={{ width: "40px", height: "auto", marginBottom: "2rem" }}
          />
        </Sidebar>
        <ContentArea>
          <LoadingSpinner>Cargando detalles de la oferta...</LoadingSpinner>
        </ContentArea>
      </PageContainer>
    );
  }

  if (error || !offer) {
    return (
      <PageContainer>
        <Sidebar>
          <img
            src={sidebarLogo}
            alt="Logo"
            style={{ width: "40px", height: "auto", marginBottom: "2rem" }}
          />
        </Sidebar>
        <ContentArea>
          <ErrorBanner>{error || "No se pudo cargar la oferta"}</ErrorBanner>
          <ActionButton onClick={() => navigate("/offers")}>
            Volver a la lista
          </ActionButton>
        </ContentArea>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Sidebar>
        <img
          src={sidebarLogo}
          alt="Logo"
          style={{ width: "40px", height: "auto", marginBottom: "2rem" }}
        />
      </Sidebar>
      <ContentArea>
        <FormHeader>
          <HeaderLeft>
            <HeaderTitle>
              Oferta Vinculante:{" "}
              <StatusInTitle>{getStatusText(offer.status)}</StatusInTitle>
            </HeaderTitle>
            <OfferIdText>ID #{id}</OfferIdText>
          </HeaderLeft>
          <ActionButtons>
            {canShowLegalCertificate() && (
              <EditButton onClick={handleDownloadLegalCertificate}>
                Descargar Certificado Procedibilidad
              </EditButton>
            )}
            {canShowSigningButton() && (
              <EditButton onClick={handleOpenSigningModal}>
                Firmar
              </EditButton>
            )}
            {canShowDownloadSignedAgreement() && (
              <EditButton onClick={handleDownloadSignedAgreement}>
                Descargar Acuerdo
              </EditButton>
            )}
            {isSigningInProgress() && (
              <ActionButton primary disabled>
                Firmando...
              </ActionButton>
            )}
            {canEdit() && (
              <EditButton onClick={() => navigate(`/editar-oferta/${id}`)}>
                Editar
              </EditButton>
            )}
            {canShowDownloadPDF() && (
              <DownloadButton onClick={handleDownloadPDF}>
                Descargar PDF
              </DownloadButton>
            )}
          </ActionButtons>
        </FormHeader>

        {/* Timeline del historial de la oferta */}
        <OfferTimeline offer={offer} />

        <ContentContainer>
          {/* Contenedor horizontal para las secciones de datos de las partes */}
          <HorizontalSections>
            {/* Sección datos ofertante */}
            <FormSection>
              <FormWrapper>
                {renderSectionTitle("Datos de la parte ofertante", "offeror")}
                <SectionDescription>
                  Datos de la persona o empresa solicitante/demandante o de tu
                  cliente, si eres su abogado o representante
                </SectionDescription>

                {renderReadOnlyField(
                  "Nombre / Razón social (Empresas)",
                  offer.offeror?.firstName || offer.representedparty_name
                )}
                {renderReadOnlyField(
                  "Apellidos / Forma societaria (SA, SL…)",
                  offer.offeror?.lastNames || offer.representedparty_last_name
                )}
                {renderReadOnlyField(
                  "DNI, NIE, NIF, Pasaporte",
                  offer.offeror?.idValue || offer.representedparty_id
                )}
                {renderReadOnlyField(
                  "Mail",
                  offer.offeror?.email || offer.representedparty_email
                )}
                {renderReadOnlyField(
                  "Móvil",
                  offer.offeror?.mobile || offer.representedparty_phone,
                  true
                )}
              </FormWrapper>
            </FormSection>

            {/* Sección datos contraparte y representación */}
            <div>
              {/* Sección datos contraparte */}
              <FormSection>
                <FormWrapper>
                  {renderSectionTitle("Datos de la contraparte", "counterparty")}
                  <SectionDescription>
                    Datos de la persona o empresa receptora de la oferta
                    vinculante
                  </SectionDescription>

                  {renderReadOnlyField(
                    "Nombre / Razón social (Empresas)",
                    offer.counterparty?.firstName || offer.counterparty_name
                  )}
                  {renderReadOnlyField(
                    "Apellidos / Forma societaria (SA, SL…)",
                    offer.counterparty?.lastNames ||
                      offer.counterparty_last_name
                  )}
                  {renderReadOnlyField(
                    "DNI, NIE, NIF, Pasaporte",
                    offer.counterparty?.idValue || offer.counterparty_id
                  )}
                  {renderReadOnlyField(
                    "Mail",
                    offer.counterparty?.email || offer.counterparty_email
                  )}
                  {renderReadOnlyField(
                    "Móvil",
                    offer.counterparty?.mobile || offer.counterparty_phone,
                    true
                  )}
                </FormWrapper>
              </FormSection>

              {/* Sección representación de la contraparte - Solo mostrar si hay datos */}
              {shouldShowRepresentacionContraparte() && (
                <FormSection>
                  <ToggleContainer>
                    {renderSectionTitle("Datos de la representación de la contraparte", "counterparty_representative", true)}
                    <ToggleSwitch>
                      <ToggleInput
                        type="checkbox"
                        checked={true}
                        readOnly
                        disabled
                      />
                      <ToggleSlider />
                    </ToggleSwitch>
                  </ToggleContainer>
                  <SectionDescription style={{ minHeight: "auto" }}>
                    Datos de la representación de la contraparte, opcionales
                  </SectionDescription>

                  {renderReadOnlyField(
                    "Nombre",
                    offer.lawyer?.firstName || offer.lawyer_name
                  )}
                  {renderReadOnlyField(
                    "Apellidos",
                    offer.lawyer?.lastNames || offer.lawyer_last_name
                  )}
                  {renderReadOnlyField(
                    "DNI, NIE",
                    offer.lawyer?.idValue || offer.lawyer_id
                  )}
                  {renderReadOnlyField(
                    "Mail",
                    offer.lawyer?.email || offer.lawyer_email
                  )}
                  {renderReadOnlyField(
                    "Móvil",
                    offer.lawyer?.mobile || offer.lawyer_phone,
                    true
                  )}
                </FormSection>
              )}
            </div>
          </HorizontalSections>

          {/* Layout de dos columnas para consideraciones y contenido */}
          <ContentLayout>
            {/* Columna izquierda: Consideraciones de la oferta */}
            <LeftColumn>
              <FormSection>
                <FormWrapper>
                  <SectionTitle>Consideraciones de la oferta</SectionTitle>
                  <SectionDescription>
                    Detalles específicos y condiciones de la oferta vinculante
                  </SectionDescription>

                  {renderReadOnlyField(
                    "Referencia propia",
                    offer.reference || offer.offer_reference
                  )}
                  {renderReadOnlyField(
                    "Objeto de la controversia",
                    offer.subject || offer.offer_subject
                  )}
                  {renderReadOnlyField(
                    "Cuantía objeto de la controversia",
                    formatAmount(offer.amount || offer.offer_total_amount)
                  )}
                  {renderReadOnlyField(
                    "Confidencialidad",
                    offer.confidential || offer.offer_confidentiality
                      ? "Sí"
                      : "No"
                  )}
                  {renderReadOnlyField(
                    "Plazo de respuesta",
                    formatDate(
                      offer.deadlineToRespond || offer.offer_response_deadline
                    ),
                    true
                  )}
                  
                  <InfoMessage>
                    Recuerda que para formular Ofertas Vinculantes cuya cuantía objeto de la controversia supere los 2.000€, la Ley exige la asistencia letrada
                  </InfoMessage>
                </FormWrapper>
              </FormSection>
            </LeftColumn>

            {/* Columna derecha: Oferta, Contraoferta y Reformulación */}
            <RightColumn>
              {/* Oferta */}
              <FormSection>
                <FormWrapper>
                  <SectionTitle>
                    Redactar oferta
                    {(offer.confidential || offer.offer_confidentiality) && (
                      <ConfidentialText style={{ marginLeft: "0.5rem" }}>
                        Confidencial
                      </ConfidentialText>
                    )}
                  </SectionTitle>
                  <SectionDescription>
                    Contenido de la oferta vinculante
                  </SectionDescription>

                  {/* Mostrar archivos adjuntos si existen */}
                  {offer.files && offer.files.length > 0 && (
                    <div>
                      {offer.files.map((file) => (
                        <FilePreview key={file.id}>
                          <FileIcon>📄</FileIcon>
                          <EditorFileName>{file.name}</EditorFileName>
                          {canEdit() ? (
                            <EditorRemoveFileButton
                              onClick={() => handleDeleteFile(file.id)}
                              disabled={isDeletingFile}
                              title="Eliminar archivo"
                            >
                              {isDeletingFile ? "..." : "✕"}
                            </EditorRemoveFileButton>
                          ) : (
                            <EditorRemoveFileButton
                              onClick={() => handleDownloadFile(file)}
                              title="Descargar archivo"
                            >
                              📁
                            </EditorRemoveFileButton>
                          )}
                        </FilePreview>
                      ))}
                    </div>
                  )}

                  {/* Contenido escrito */}
                  {offer.writtenContent || offer.offer_user_content ? (
                    <RichTextEditor
                      value={offer.writtenContent || offer.offer_user_content}
                      readOnly
                      minHeight="200px"
                    />
                  ) : (
                    !offer.files?.length && (
                      <RichTextEditor
                        value="Sin contenido escrito"
                        readOnly
                        minHeight="200px"
                        style={{ opacity: 0.6 }}
                      />
                    )
                  )}
                </FormWrapper>
              </FormSection>

              {/* Contraoferta - Solo mostrar si el estado es contraofertada */}
              {canShowCounterOffer() && (
                <FormSection>
                  <FormWrapper>
                    <SectionTitle>Contraoferta</SectionTitle>
                    <SectionDescription>
                      Respuesta de la contraparte a la oferta original
                    </SectionDescription>

                    {/* Mostrar archivos adjuntos de la contraoferta si existen */}
                    {offer.counterOfferFiles &&
                      offer.counterOfferFiles.length > 0 && (
                        <div>
                          {offer.counterOfferFiles.map((file, index) => (
                            <FilePreview key={index}>
                              <FileIcon>📄</FileIcon>
                              <EditorFileName>{file.name}</EditorFileName>
                              <EditorRemoveFileButton
                                onClick={() => handleDownloadFile(file)}
                              >
                                📁
                              </EditorRemoveFileButton>
                            </FilePreview>
                          ))}
                        </div>
                      )}

                    {/* Contenido escrito de la contraoferta */}
                    {offer.counteroffer_text ? (
                      <RichTextEditor
                        value={offer.counteroffer_text}
                        readOnly
                        minHeight="150px"
                      />
                    ) : (
                      !offer.counterOfferFiles?.length && (
                        <RichTextEditor
                          value="Sin contenido escrito en la contraoferta"
                          readOnly
                          minHeight="150px"
                          style={{ opacity: 0.6 }}
                        />
                      )
                    )}
                  </FormWrapper>
                </FormSection>
              )}

              {/* Formulario de reformulación - Solo para estado counteroffered */}
              {shouldShowReformulation() && (
                <FormSection>
                  <FormWrapper>
                    <SectionTitle>Reformulación de la oferta</SectionTitle>
                    <SectionDescription>
                      Reformula tu oferta en respuesta a la contraoferta recibida. Puedes redactar la reformulación directamente o adjuntar documentos digitales en formato word o pdf. Solo puedes usar uno de los dos métodos.
                    </SectionDescription>

                    {/* Campo para nueva fecha límite */}
                    <DateField>
                      <DateLabel>Nueva fecha límite de respuesta</DateLabel>
                      <DateInput
                        type="date"
                        value={newDeadline}
                        onChange={(e) => setNewDeadline(e.target.value)}
                        disabled={isSubmitting}
                        min={getMinDeadlineDate()}
                      />
                    </DateField>

                    {/* Input oculto para archivos */}
                    <FileInputHidden
                      type="file"
                      id="reformulation-file-upload"
                      accept=".pdf,.doc,.docx"
                      multiple
                      onChange={handleReformulationFileChange}
                      disabled={!isRichTextEmpty(reformulationText) || isSubmitting}
                    />

                    {/* Botón de adjuntar archivo */}
                    {!isRichTextEmpty(reformulationText) ? (
                      <DisabledAdjuntarButton
                        onClick={() => {
                          // El botón ya indica visualmente que no se puede usar
                        }}
                        disabled={isSubmitting}
                      >
                        <ClipIcon>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                          </svg>
                        </ClipIcon>
                        Adjuntar archivo (primero elimina el texto)
                        <ArrowIcon>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <polygon points="12 17 4 7 20 7" />
                          </svg>
                        </ArrowIcon>
                      </DisabledAdjuntarButton>
                    ) : (
                      <AdjuntarButton
                        onClick={() =>
                          !isSubmitting && document.getElementById("reformulation-file-upload").click()
                        }
                        disabled={isSubmitting}
                      >
                        <ClipIcon>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                          </svg>
                        </ClipIcon>
                        Adjuntar archivo
                        <ArrowIcon>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <polygon points="12 17 4 7 20 7" />
                          </svg>
                        </ArrowIcon>
                      </AdjuntarButton>
                    )}

                    {/* Mostrar archivos adjuntos */}
                    {reformulationFiles.length > 0 && (
                      <div>
                        {reformulationFiles.map((file, index) => (
                          <FilePreview key={index}>
                            <FileIcon>📄</FileIcon>
                            <FileName>{file.name}</FileName>
                            {!isSubmitting && (
                              <RemoveFileButton
                                onClick={() => handleRemoveReformulationFile(index)}
                                title="Eliminar archivo"
                              >
                                ✕
                              </RemoveFileButton>
                            )}
                          </FilePreview>
                        ))}
                      </div>
                    )}

                    {/* Campo de texto para la reformulación */}
                    {reformulationFiles.length > 0 ? (
                      <RichTextEditor
                        key="reformulation-disabled"
                        value=""
                        placeholder="Para escribir texto, elimina primero los archivos adjuntos"
                        disabled={true}
                        readOnly={true}
                        minHeight="150px"
                      />
                    ) : (
                      <RichTextEditor
                        key="reformulation-enabled"
                        value={reformulationText}
                        onChange={(content) => setReformulationText(content)}
                        placeholder="Redacta aquí la reformulación de tu oferta"
                        disabled={isSubmitting}
                        minHeight="150px"
                      />
                    )}

                    {/* Mostrar errores */}
                    {error && <ErrorMessage>{error}</ErrorMessage>}

                    <ReformulationActions>
                      <ConfirmationCheckbox>
                        <CheckboxInput
                          type="checkbox"
                          checked={acceptsLegalTerms}
                          onChange={(e) =>
                            setAcceptsLegalTerms(e.target.checked)
                          }
                          disabled={isSubmitting}
                          id="terms-checkbox"
                        />
                        <label htmlFor="terms-checkbox">
                          Acepto los{" "}
                          <a href="https://ofertasvinculantes.es/condiciones-generales" target="_blank" rel="noopener noreferrer">
                            condiciones de contratación
                          </a>{" "}
                          y confirmo que he leído la{" "}
                          <a href="https://ofertasvinculantes.es/politica-de-privacidad" target="_blank" rel="noopener noreferrer">
                            política de privacidad
                          </a>
                        </label>
                      </ConfirmationCheckbox>

                      <ReformulationButton
                        onClick={handleSubmitReformulation}
                        disabled={
                          isSubmitting ||
                          (isRichTextEmpty(reformulationText) && reformulationFiles.length === 0) ||
                          !newDeadline ||
                          !acceptsLegalTerms
                        }
                      >
                        {isSubmitting ? "Enviando..." : "Enviar Reformulación"}
                      </ReformulationButton>
                    </ReformulationActions>
                  </FormWrapper>
                </FormSection>
              )}

              {/* Reformulación en modo lectura - Para estados con reformulación */}
              {shouldShowReformulationReadOnly() && (
                <FormSection>
                  <FormWrapper>
                    <SectionTitle>Reformulación de la oferta</SectionTitle>
                    <SectionDescription>
                      Reformulación enviada en respuesta a la contraoferta recibida.
                    </SectionDescription>

                    {/* Mostrar fecha límite de reformulación */}
                    {offer.reformulation_deadline && (
                      <div style={{ marginBottom: "1rem" }}>
                        <strong>Nueva fecha límite de respuesta:</strong>{" "}
                        {formatDate(offer.reformulation_deadline)}
                      </div>
                    )}

                    {/* Mostrar archivos adjuntos de la reformulación si existen */}
                    {offer.reformulationFiles && offer.reformulationFiles.length > 0 && (
                      <div>
                        {offer.reformulationFiles.map((file, index) => (
                          <FilePreview key={index}>
                            <FileIcon>📄</FileIcon>
                            <EditorFileName>{file.name}</EditorFileName>
                            <EditorRemoveFileButton
                              onClick={() => handleDownloadReformulationFile(file)}
                              title="Descargar archivo"
                            >
                              📁
                            </EditorRemoveFileButton>
                          </FilePreview>
                        ))}
                      </div>
                    )}

                    {/* Contenido escrito de la reformulación */}
                    {offer.reformulation_content || offer.reformulationText ? (
                      <RichTextEditor
                        value={offer.reformulation_content || offer.reformulationText}
                        readOnly
                        minHeight="150px"
                      />
                    ) : (
                      !offer.reformulationFiles?.length && (
                        <RichTextEditor
                          value="Sin contenido escrito en la reformulación"
                          readOnly
                          minHeight="150px"
                          style={{ opacity: 0.6 }}
                        />
                      )
                    )}
                  </FormWrapper>
                </FormSection>
              )}
            </RightColumn>
          </ContentLayout>
        </ContentContainer>

        <Footer />
      </ContentArea>
      
      {/* Modal de captura de direcciones para firma */}
      <SigningAddressModal
        isOpen={isSigningModalOpen}
        onClose={handleCloseSigningModal}
        onSubmit={handleInitiateSigning}
        offer={offer}
        isSubmitting={isInitiatingSigning}
        error={signingError}
      />
    </PageContainer>
  );
};

export default OfferDetailPage;
